import { Configuration } from "./Configuration";
import { MatchOperator, PatternHelp } from "./MatchOperator";
import {
  freeform,
  parseProject,
  fieldSchema,
  newField,
  fieldTypes,
  reusableFormSchema,
  reusableRuleSchema,
  reusablePresetSchema,
  type Field,
  type Project,
} from "@proof/core";
import { Select, Button, Control, Toggle } from "./components";
import { ImageChoices } from "./ImageChoices";

const inputTypes = fieldTypes.filter((type) => type !== "preset");
export function FormLibraryEditor({
  value,
  onChange,
  profileCollection = false,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  profileCollection?: boolean;
}) {
  const form = value as ReturnType<typeof reusableFormSchema.parse>;
  if (!profileCollection) {
    const project = parseProject(
      form.extra_json_data?.builder || {
        ...freeform,
        id: "global-form-builder",
        name: "Global form",
        pages: freeform.pages.map((page) => ({ ...page, nodes: [] })),
        fields: form.fields,
        groups: [],
        tabs: [],
        triggers: [],
        parts: [],
        forms: [],
        formInstances: [],
      },
    );
    return (
      <>
      <Toggle
        label="Auto-map existing variables; hide unmatched inputs"
        value={form.application !== "copy"}
        disabled={!!form.profileSource}
        onChange={(enabled) =>
          onChange({ ...form, application: enabled ? "mapped" : "copy" })
        }
      />
      <Configuration
        project={project}
        notify={(message) => window.alert(message)}
        onChange={(next) => {
          const fields = next.fields.map((field) =>
            fieldSchema.parse({
              ...field,
              source: { kind: "literal" },
              calculation: undefined,
              groupId: undefined,
              visibleWhen: undefined,
              presets: [],
              qr: field.qr
                ? { ...field.qr, mapping: {}, defaults: {} }
                : undefined,
              map: field.map
                ? { ...field.map, mapping: {}, defaults: {}, linked: {} }
                : undefined,
            }),
          );
          onChange({
            ...form,
            fields,
            extra_json_data: {
              ...form.extra_json_data,
              version: 1,
              builder: next,
            },
          });
        }}
      />
      </>
    );
  }
  const change = (index: number, patch: Partial<Field>) =>
    onChange({
      ...form,
      fields: form.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    });
  return (
    <div>
      <p>
        {profileCollection
          ? "Define reusable profile inputs. Projects map these inputs to their own variables; saved personal values are stored separately."
          : "Create independent inputs once, then apply them to any document. Match inputs to existing document variables, then expose any additional inputs you need."}
      </p>
      {!profileCollection && (
        <Toggle
          label="Auto-map existing variables; hide unmatched inputs"
          value={form.application !== "copy"}
          disabled={!!form.profileSource}
          onChange={(enabled) =>
            onChange({ ...form, application: enabled ? "mapped" : "copy" })
          }
        />
      )}
      {form.fields.map((f, i) => (
        <article className="part-card" key={f.id}>
          <div className="control-grid">
            <Control label={`Input ${i + 1} label`}>
              <input
                value={f.label}
                onChange={(e) => change(i, { label: e.target.value })}
              />
            </Control>
            <Control label={`Input ${i + 1} variable key`}>
              <input
                value={f.key}
                onChange={(e) => change(i, { key: e.target.value })}
              />
            </Control>
            <Control label={`Input ${i + 1} type`}>
              <Select
                value={f.type}
                onChange={(e) =>
                  change(i, {
                    type: e.target.value as Field["type"],
                    default: e.target.value === "checkbox" ? false : "",
                  })
                }
              >
                {inputTypes.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </Select>
            </Control>
            <Control label={`Input ${i + 1} help`}>
              <input
                value={f.help}
                onChange={(e) => change(i, { help: e.target.value })}
              />
            </Control>
            <Control label={`Input ${i + 1} default`}>
              {f.type === "checkbox" ? (
                <Select
                  value={String(f.default)}
                  onChange={(e) =>
                    change(i, { default: e.target.value === "true" })
                  }
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </Select>
              ) : (
                <input
                  type={f.type === "number" ? "number" : "text"}
                  value={String(f.default ?? "")}
                  onChange={(e) =>
                    change(i, {
                      default:
                        f.type === "number" && e.target.value !== ""
                          ? Number(e.target.value)
                          : e.target.value,
                    })
                  }
                />
              )}
            </Control>
          </div>
          <Toggle
            label={`Input ${i + 1} required`}
            value={f.required}
            onChange={(required) => change(i, { required })}
          />
          <Toggle
            label={`Input ${i + 1} editable`}
            value={f.editable}
            onChange={(editable) => change(i, { editable })}
          />
          <Toggle
            label={`Input ${i + 1} hidden on form`}
            value={f.formDisplay === "hide"}
            onChange={(hidden) =>
              change(i, { formDisplay: hidden ? "hide" : "show" })
            }
          />
          {["dropdown", "combobox", "radio", "image-list"].includes(f.type) && (
            <Control
              label={`Input ${i + 1} options`}
              note="One value per line; labels use the same text."
            >
              <textarea
                value={f.choices.map((c) => c.value).join("\n")}
                onChange={(e) =>
                  change(i, {
                    choices: e.target.value
                      .split("\n")
                      .filter(Boolean)
                      .map((s) => ({
                        ...f.choices.find((choice) => choice.value === s),
                        value: s,
                        label: s,
                      })),
                  })
                }
              />
            </Control>
          )}
          {f.type === "image-list" && (
            <>
              <Control label={`Input ${i + 1} image display`}>
                <Select
                  value={f.choicePresentation || "select"}
                  onChange={(event) =>
                    change(i, {
                      choicePresentation: event.target
                        .value as Field["choicePresentation"],
                    })
                  }
                >
                  <option value="select">Dropdown</option>
                  <option value="thumbnails">Thumbnail gallery</option>
                </Select>
              </Control>
              <ImageChoices field={f} onChange={(patch) => change(i, patch)} />
            </>
          )}
          <Button
            disabled={form.fields.length === 1}
            onClick={() =>
              onChange({
                ...form,
                fields: form.fields.filter((_, j) => j !== i),
                profileSource: undefined,
              })
            }
          >
            Remove input {i + 1}
          </Button>
        </article>
      ))}
      <Button
        disabled={form.fields.length >= 100}
        onClick={() => {
          const field = newField("text", form.fields.length);
          while (form.fields.some((f) => f.key === field.key))
            field.key += "_copy";
          onChange({ ...form, fields: [...form.fields, field] });
        }}
      >
        Add form input
      </Button>
    </div>
  );
}
function TargetKeys({
  label,
  keys,
  project,
  onChange,
}: {
  label: string;
  keys: string[];
  project: Project;
  onChange: (v: string[]) => void;
}) {
  return (
    <fieldset>
      <legend>{label}</legend>
      {project.fields.map((f) => (
        <Toggle
          key={f.id}
          label={`${label}: ${f.label}`}
          value={keys.includes(f.key)}
          onChange={(checked) =>
            onChange(
              checked ? [...keys, f.key] : keys.filter((k) => k !== f.key),
            )
          }
        />
      ))}
      {keys
        .filter((k) => !project.fields.some((f) => f.key === k))
        .map((k) => (
          <p key={k}>
            Missing variable: {k}{" "}
            <Button onClick={() => onChange(keys.filter((x) => x !== k))}>
              Remove missing target {k}
            </Button>
          </p>
        ))}
    </fieldset>
  );
}
export function RuleLibraryEditor({
  value,
  project,
  onChange,
}: {
  value: unknown;
  project: Project;
  onChange: (v: unknown) => void;
}) {
  const rule = value as ReturnType<typeof reusableRuleSchema.parse>;
  return (
    <div>
      <p>
        When a variable matches the configured comparison, show, hide, or clear
        target variables. Shared rules use variable keys across projects.
        Applying a saved version creates this project's visibility settings;
        editing the shared rule does not change visibility in projects that
        already use it.
      </p>
      <Control label="Rule source variable">
        <Select
          value={rule.sourceKey}
          onChange={(e) => onChange({ ...rule, sourceKey: e.target.value })}
        >
          <option value={rule.sourceKey}>
            {project.fields.find((f) => f.key === rule.sourceKey)?.label ||
              rule.sourceKey}
          </option>
          {project.fields
            .filter((f) => f.key !== rule.sourceKey)
            .map((f) => (
              <option key={f.id} value={f.key}>
                {f.label}
              </option>
            ))}
        </Select>
      </Control>
      <MatchOperator
        label="Shared rule operator"
        value={rule.op}
        onChange={(op) => onChange({ ...rule, op })}
      />
      <PatternHelp op={rule.op} value={rule.value} />
      <Control label="Rule match type">
        <Select
          value={rule.value === null ? "null" : typeof rule.value}
          onChange={(e) =>
            onChange({
              ...rule,
              value:
                e.target.value === "number"
                  ? 0
                  : e.target.value === "boolean"
                    ? false
                    : e.target.value === "null"
                      ? null
                      : "",
            })
          }
        >
          <option value="string">Text</option>
          <option value="number">Number</option>
          <option value="boolean">Boolean</option>
          <option value="null">Empty / null</option>
        </Select>
      </Control>
      {typeof rule.value === "boolean" ? (
        <Control label="Rule match value">
          <Select
            value={String(rule.value)}
            onChange={(e) =>
              onChange({ ...rule, value: e.target.value === "true" })
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Select>
        </Control>
      ) : (
        rule.value !== null && (
          <Control label="Rule match value">
            <input
              type={typeof rule.value === "number" ? "number" : "text"}
              value={String(rule.value)}
              onChange={(e) =>
                onChange({
                  ...rule,
                  value:
                    typeof rule.value === "number"
                      ? Number(e.target.value)
                      : e.target.value,
                })
              }
            />
          </Control>
        )
      )}
      <div className="control-grid">
        {(["show", "hide", "clear"] as const).map((k) => (
          <TargetKeys
            key={k}
            label={`Rule ${k}`}
            keys={rule[k]}
            project={project}
            onChange={(keys) => onChange({ ...rule, [k]: keys })}
          />
        ))}
      </div>
    </div>
  );
}
export function PresetLibraryEditor({
  value,
  project,
  onChange,
}: {
  value: unknown;
  project: Project;
  onChange: (v: unknown) => void;
}) {
  const preset = value as ReturnType<typeof reusablePresetSchema.parse>;
  const change = (i: number, patch: Partial<(typeof preset.options)[number]>) =>
    onChange({
      options: preset.options.map((o, j) => (i === j ? { ...o, ...patch } : o)),
    });
  return (
    <div>
      <p>
        Save named sets of values. Applying creates a preset selector bound to
        matching variable keys.
      </p>
      {preset.options.map((o, i) => (
        <article className="part-card" key={o.id}>
          <Control label={`Option ${i + 1} label`}>
            <input
              value={o.label}
              onChange={(e) => change(i, { label: e.target.value })}
            />
          </Control>
          <Control label={`Option ${i + 1} match variable`}>
            <Select
              value={o.match?.field || ""}
              onChange={(e) =>
                change(i, {
                  match: e.target.value
                    ? { field: e.target.value, op: "eq", value: "" }
                    : undefined,
                })
              }
            >
              <option value="">Manual selection only</option>
              {project.fields
                .filter((f) => f.key)
                .map((f) => (
                  <option key={f.id} value={f.key}>
                    {f.label}
                  </option>
                ))}
            </Select>
          </Control>
          {o.match && (
            <>
              <MatchOperator
                label={`Option ${i + 1} match operator`}
                value={o.match.op}
                onChange={(op) => change(i, { match: { ...o.match!, op } })}
              />
              <Control label={`Option ${i + 1} match value`}>
                <input
                  value={o.match.value}
                  onChange={(e) =>
                    change(i, { match: { ...o.match!, value: e.target.value } })
                  }
                />
              </Control>
              <PatternHelp op={o.match.op} value={o.match.value} />
              <p className="muted">
                With no manual selection, the first matching option supplies
                values. Matching is case-sensitive.
              </p>
            </>
          )}
          {Object.entries(o.values).map(([key, value]) => (
            <div className="library-input-row" key={key}>
              <Control label={`Option ${i + 1} value for ${key}`}>
                {typeof value === "boolean" ? (
                  <Select
                    value={String(value)}
                    onChange={(e) =>
                      change(i, {
                        values: {
                          ...o.values,
                          [key]: e.target.value === "true",
                        },
                      })
                    }
                  >
                    <option value="false">False</option>
                    <option value="true">True</option>
                  </Select>
                ) : (
                  <input
                    type={typeof value === "number" ? "number" : "text"}
                    value={String(value ?? "")}
                    onChange={(e) =>
                      change(i, {
                        values: {
                          ...o.values,
                          [key]:
                            typeof value === "number"
                              ? Number(e.target.value)
                              : e.target.value,
                        },
                      })
                    }
                  />
                )}
              </Control>
              <Button
                onClick={() =>
                  change(i, {
                    values: Object.fromEntries(
                      Object.entries(o.values).filter(([k]) => k !== key),
                    ),
                  })
                }
              >
                Remove target {key}
              </Button>
            </div>
          ))}
          <Control label={`Option ${i + 1} add variable`}>
            <Select
              value=""
              onChange={(e) => {
                const f = project.fields.find((f) => f.key === e.target.value);
                if (f)
                  change(i, {
                    values: {
                      ...o.values,
                      [f.key]:
                        f.type === "checkbox"
                          ? false
                          : f.type === "number"
                            ? 0
                            : "",
                    },
                  });
              }}
            >
              <option value="">Choose a target</option>
              {project.fields
                .filter(
                  (f) =>
                    f.editable &&
                    !f.system &&
                    ["literal", "asset"].includes(f.source.kind) &&
                    f.type !== "preset" &&
                    !Object.hasOwn(o.values, f.key),
                )
                .map((f) => (
                  <option key={f.id} value={f.key}>
                    {f.label}
                  </option>
                ))}
            </Select>
          </Control>
          <Button
            disabled={preset.options.length === 1}
            onClick={() =>
              onChange({ options: preset.options.filter((_, j) => i !== j) })
            }
          >
            Remove option {i + 1}
          </Button>
        </article>
      ))}
      <Button
        onClick={() =>
          onChange({
            options: [
              ...preset.options,
              { id: crypto.randomUUID(), label: "New option", values: {} },
            ],
          })
        }
      >
        Add saved preset option
      </Button>
    </div>
  );
}
